import { useAtomValue } from 'jotai';
import { Send } from 'lucide-react';
import Summary from '@modules/project/component/summary/Summary';
import { ProjectBreadcrumb } from '@modules/project/component/breadcrumb/Breadcrumb';
import type { ProjectPageLoader } from '@modules/project/type';
import {
    Accordion,
    AccordionContent,
    AccordionTrigger,
    AccordionItem,
} from '@rocco/components/accordion';
import { DetailsTable } from '@modules/project/component/details/DetailsTable';
import { Button } from '@rocco/components/button';
import { ProjectTeam } from './ProjectTeam';
import { uiContextAtomSocialSharingActions } from '@rocco/ui/social-sharing/components/SocialSharingActions';
import { ReactComponent as ShortlistIcon } from '@rocco/icons/svg/shortlist.svg';
import { useHandleFollow } from '~/modules-rocco/design-board/hook/use-handle-follow';
import { useUser } from '~/modules/user';
import { hasFeature } from '~/modules/root/util';
import { useProjectPreviewRouteData } from '../../hook/use-project-preview-route-data';
import { useEnquiryModal } from '~/modules-rocco/enquiry/hooks/use-enquiry-modal';
/**
 * Accordion component that displays different sections of project details including team members
 */

interface ProjectDetailsAccordionProps {
    projects: ProjectPageLoader['project'];
}

export const ProjectDetailsAccordion = ({
    projects,
}: ProjectDetailsAccordionProps) => {
    const project = useProjectPreviewRouteData();
    const user = useUser();
    const showProjectTeam = hasFeature(user.FeatureFlags, 'project_teams');

    const socialSharingActions = useAtomValue(
        uiContextAtomSocialSharingActions
    );

    const { openEnquiryModal } = useEnquiryModal({
        professional: projects.Professional,
        itemType: 'professional',
        siteTreeID: projects.ID,
        relatedItem: {
            id: projects.ID,
            type: 'project',
            title: projects.Title,
            category: projects.categoryPath,
        },
    });

    const handleShareClick = () => {
        if (!socialSharingActions) return;
        socialSharingActions.showSocialSharingModal();
    };
    const { handleFollow, isInDesignBoard } = useHandleFollow();

    const isShortlisted = isInDesignBoard(project.ID, 'ProjectPin');

    return (
        <div className="relative px-12 pt-12">
            <div className="absolute right-0 top-0 flex gap-2 px-12 pt-12">
                <Button
                    className="border-gray-100 bg-gray-100 text-gray-900 hover:border-gray-200 hover:bg-gray-200"
                    onClick={handleShareClick}
                >
                    Share
                    <Send className="ml-2 size-4" />
                </Button>
                <Button
                    className="border-gray-100 bg-gray-100 text-gray-900 hover:border-gray-200 hover:bg-gray-200"
                    onClick={() =>
                        handleFollow(
                            {
                                id: project.ID,
                            },
                            'ProjectPin'
                        )
                    }
                    disabled={isShortlisted}
                >
                    {isShortlisted ? 'Shortlisted' : 'Shortlist'}
                    <ShortlistIcon
                        className="ml-2 size-4"
                        style={{
                            fill: isShortlisted ? '#000' : '',
                        }}
                    />
                </Button>
            </div>
            <div className={'grid min-h-20 grid-cols-[19.8125rem_1fr] gap-4'}>
                {project.Content ? (
                    <>
                        <div>
                            <span className="text-lg font-medium">About</span>
                        </div>
                        <div>
                            <Summary data={project} />
                        </div>
                    </>
                ) : null}
            </div>
            <div className="flex justify-end pb-3">
                <ProjectBreadcrumb
                    breadcrumbs={[
                        { href: '/', label: 'Home' },
                        ...project.Breadcrumbs.map((b) => ({
                            href: b.href,
                            label: b.label,
                        })),
                    ]}
                />
            </div>
            <Accordion
                type="multiple"
                className="divide-y divide-gray-900 border-b border-t border-b-gray-900 border-t-gray-900"
                defaultValue={['project-details']}
            >
                {makeAccordionItem({
                    key: 'project-details',
                    title: 'Project Details',
                    content: (
                        <DetailsTable
                            data={project}
                            openEnquiryModal={openEnquiryModal}
                        />
                    ),
                })}
                {showProjectTeam &&
                    makeAccordionItem({
                        key: 'project-team',
                        title: 'Project Team',
                        content: <ProjectTeam />,
                    })}
            </Accordion>
        </div>
    );
};

const makeAccordionItem = ({
    key,
    title,
    content,
}: {
    key: string;
    title?: string;
    content: React.ReactNode;
}) => {
    return (
        <AccordionItem key={key} value={key} variant="noBorder">
            <AccordionTrigger icon="plus" openIcon="minus">
                <span className="text-lg font-medium tracking-[0.025rem]">
                    {title}
                </span>
            </AccordionTrigger>
            <AccordionContent>{content}</AccordionContent>
        </AccordionItem>
    );
};
